import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNode, useEditor } from "@craftjs/core";

interface RenderNodeProps {
  render: React.ReactNode;
  setIsComponentsPanelOpen: any;
}

export const RenderNode = ({ render, setIsComponentsPanelOpen }: RenderNodeProps) => {
  const {
    id,
    isResizable,
    dom,
    actions: { setProp },
  } = useNode((node) => ({
    dom: node.dom,
    isResizable: node.data.props.isResizable || false,
  }));

  const {
    isActive,
    actions: { selectNode },
  } = useEditor((_, query) => ({
    isActive: query.getEvent("selected").contains(id),
  }));

  const [isResizing, setIsResizing] = useState(false);
  const [startY, setStartY] = useState(0);
  const [initialHeight, setInitialHeight] = useState<number | undefined>(undefined);
  const [currentHeight, setCurrentHeight] = useState(0);

  const previewContainerRef = useRef<HTMLDivElement | null>(null); // Reference to the preview container

  // Use this aspect ratio for the height calculation
  const aspectRatio = 16 / 9;

  useEffect(() => {
    // Initialize the preview container reference, cast to HTMLDivElement
    previewContainerRef.current = document.getElementById("preview-container") as HTMLDivElement | null; // Assuming the preview container has an id 'preview-container'
  }, []);

  // Handle mousemove event to resize the element
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !initialHeight || !dom || !isResizable || !previewContainerRef.current) return;

      const previewWidth = previewContainerRef.current.getBoundingClientRect().width;

      // Calculate maximum allowed height based on the preview aspect ratio
      const maxAllowedHeight = previewWidth / aspectRatio;

      // Calculate the new height in pixels
      const newHeightPx = initialHeight + (e.clientY - startY);

      // Get the min height of the elements content by cloning the element and setting its height to auto
      const clone = dom.cloneNode(true) as HTMLElement;
      clone.style.height = "auto";
      document.body.appendChild(clone);
      const minContentHeight = clone.offsetHeight;
      document.body.removeChild(clone);

      // Check if we are trying to expand beyond the maximum height or below the min-content height
      if (newHeightPx > maxAllowedHeight || newHeightPx < minContentHeight) {
        return; // Prevent expanding beyond max height or shrinking below min-content height
      }

      // Allow resizing if shrinking or staying within bounds
      dom.style.height = `${newHeightPx}px`;
      setCurrentHeight(newHeightPx);

      // set the sizing props
      setProp((props: any) => {
        props.sizing = {
          height: {
            value: newHeightPx + "px",
            previewVh: `${((newHeightPx / maxAllowedHeight) * 100).toFixed()}vh`,
            heightPercentage: (newHeightPx / maxAllowedHeight).toFixed(2),
          },
        };
      });
    },
    [isResizing, initialHeight, dom, isResizable, startY, setProp, aspectRatio],
  );

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!dom || !isResizable) return;

    setIsResizing(true);
    setStartY(e.clientY);
    setInitialHeight(dom.offsetHeight);
    selectNode(id);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, handleMouseMove]);

  useEffect(() => {
    if (dom) {
      if (isActive) {
        dom.classList.add("active-node");
        setIsComponentsPanelOpen(true);
      } else {
        dom.classList.remove("active-node");
      }
    }
  }, [dom, isActive, setIsComponentsPanelOpen]);

  return (
    <div className="relative">
      {isResizable && (
        <>
          {/* Resize Handles */}
          <div
            className="absolute w-full h-8 bg-transparent z-10 top-0 cursor-ns-resize"
            style={{
              display: isActive ? "block" : "none",
            }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
          />

          {render}

          <div
            className="absolute w-full h-8 bg-transparent z-10 bottom-0 cursor-ns-resize"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            style={{
              display: isActive ? "block" : "none",
            }}
          />

          {/* Ruler and Overlay to display resizing info - only visible while resizing */}
          {isResizing && previewContainerRef.current && (
            <>
              {/* Overlay to display current resizing info */}
              <div className="absolute bottom-2 right-2 select-none z-50 bg-gray-800 text-white text-sm p-2 rounded-md">
                <div>
                  <b>Current Height:&nbsp;</b>
                  {((currentHeight / (previewContainerRef.current.getBoundingClientRect().width / aspectRatio)) * 100).toFixed()}
                  vh
                </div>
                <div>
                  <b>Min Height:&nbsp;</b>
                  {currentHeight.toFixed(2)}px
                </div>
              </div>
            </>
          )}
        </>
      )}

      {!isResizable && render}
    </div>
  );
};
